import React, { MouseEvent, useCallback, useRef } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import { useState } from "react";
import { ComponentStyles } from "../../models/app";
import { blockConfigs } from "../../models/block";
import { PaletteItem } from "./PaletteItem";
import { useDispatch } from "react-redux";
import { addBlock } from "../../store/game/gameSlice";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import { BlockTypes } from "../../models/block-types";

/** Block groups displayed in the palette */
const paletteGroups: { label: string; blocks: BlockTypes[] }[] = [
  {
    label: "Compute",
    blocks: [
      BlockTypes.APP_SERVER,
      BlockTypes.AUTO_SCALING_GROUP,
      BlockTypes.LAMBDA,
    ],
  },
  {
    label: "Storage",
    blocks: [
      BlockTypes.DATABASE,
      BlockTypes.DB_READ_REPLICA,
      BlockTypes.CACHE,
      
    ],
  },
  {
    label: "Routing",
    blocks: [BlockTypes.LOAD_BALANCER, BlockTypes.NETWORK_LOAD_BALANCER],
  },
  {
    label: "Other",
    blocks: [BlockTypes.CDN, BlockTypes.QUEUE],
  },
];

const styles: ComponentStyles = {
  container: {
    position: "absolute",
    top: "120px",
    left: "0px",
    height: "70vh",
    minWidth: "70px",
    display: "flex",
    flexDirection: "row",
    transition: "left 0.5s",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "center",
    backgroundColor: "white",
    height: "100%",
    padding: "12px 8px",
    overflowY: "auto",
  },
  slideButtonContainer: {
    backgroundColor: "#595A59",
    borderBottomRightRadius: "10px",
    borderTopRightRadius: "10px",
    background: "white",
    width: "20px",
    height: "20px",
    padding: "5px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
};

export const Palette: React.FC = () => {
  const classes = styles;
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(true);
  const cardRef = useRef<HTMLDivElement>(null);

  const getCardWidth = () => cardRef.current?.offsetWidth ?? 180;
  const leftPos = isExpanded ? "0px" : `-${getCardWidth()}px`;

  const handleBlockAdd = (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
    blockType: BlockTypes,
  ) => {
    e.stopPropagation();
    const blockConfig = blockConfigs[blockType];
    if (!blockConfig) return;
    const { inputCapacity, processingCapacity } = blockConfig;
    dispatch(
      addBlock({
        top: 100,
        left: 100,
        id: "temp",
        width: 100,
        height: 100,
        blockType,
        inputCapacity,
        processingCapacity,
      }),
    );
  };

  const handleExpandedToggle = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  return (
    <Box style={{ ...classes.container, left: leftPos }}>
      <Card ref={cardRef} style={classes.card} elevation={10}>
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            width: "100%",
          }}
        >
          {paletteGroups.map((group, groupIdx) => (
            <React.Fragment key={group.label}>
              {/* Divider with inline label */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  width: "100%",
                  my: groupIdx === 0 ? "2px" : "6px",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: "1px",
                    backgroundColor: "#ccc",
                  }}
                />
                <Box
                  component="span"
                  sx={{
                    fontSize: "10px",
                    fontWeight: 700,
                    color: "#888",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {group.label}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    height: "1px",
                    backgroundColor: "#ccc",
                  }}
                />
              </Box>

              {/* 2-per-row layout of blocks */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px",
                  width: "152px",
                }}
              >
                {group.blocks.map((block) => (
                  <Box key={block} sx={{ flex: "0 0 calc(50% - 3px)" }}>
                    <PaletteItem
                      blockType={block}
                      onItemClick={handleBlockAdd}
                    />
                  </Box>
                ))}
              </Box>
            </React.Fragment>
          ))}
        </Box>
      </Card>
      <Box
        style={classes.slideButtonContainer}
        onClick={() => handleExpandedToggle()}
      >
        {isExpanded && (
          <ArrowCircleLeftIcon
            sx={{
              color: "#595A59",
              cursor: "pointer",
              "&:hover": {
                color: "#000",
              },
            }}
          />
        )}
        {!isExpanded && (
          <ArrowCircleRightIcon
            sx={{
              color: "#595A59",
              cursor: "pointer",
              "&:hover": {
                color: "#000",
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
};
